import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PeopleIcon from '@mui/icons-material/People'
import PaymentsIcon from '@mui/icons-material/Payments'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import SpaIcon from '@mui/icons-material/Spa'
import FaceIcon from '@mui/icons-material/Face'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { clearAuth, getRole, setAuthenticated } from '../lib/auth'

const DRAWER_WIDTH = 240

/**
 * Height of the mobile top app bar. Exported so full-screen overlays
 * (e.g. StudentDrawer) can position themselves below it instead of
 * being covered by it.
 */
export const MOBILE_APP_BAR_HEIGHT = 56

const ALL_NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, adminOnly: true },
  { label: 'Master List', path: '/master-list', icon: <FaceIcon />, adminOnly: true },
  { label: 'Tags', path: '/tags', icon: <LocalOfferIcon />, adminOnly: true },
  { label: 'Calendar', path: '/calendar', icon: <CalendarMonthIcon />, adminOnly: false },
  { label: 'Tuition', path: '/roster', icon: <PeopleIcon />, adminOnly: false },
  { label: 'Roll Call', path: '/rollcall', icon: <FactCheckIcon />, adminOnly: false },
  { label: 'Expenses', path: '/expenses', icon: <ReceiptLongIcon />, adminOnly: true },
  { label: 'Payroll', path: '/payroll', icon: <PaymentsIcon />, adminOnly: true }
]

/** Returns only the nav items the current role is allowed to see. */
function getNavItems(): typeof ALL_NAV_ITEMS {
  const role = getRole()
  if (role === 'viewer') {
    return ALL_NAV_ITEMS.filter((item) => !item.adminOnly)
  }
  return ALL_NAV_ITEMS
}

export default function AppLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()

  function handleLogout() {
    clearAuth()
    setAuthenticated(false)
    navigate('/login', { replace: true })
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Brand header */}
      <Box sx={{ px: 2, py: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SpaIcon sx={{ color: '#A5D6A7', fontSize: 28 }} />
        <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.2 }}>
          Farmschool
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

      {/* Nav links */}
      <List sx={{ flex: 1, pt: 1 }}>
        {getNavItems().map(({ label, path, icon }) => (
          <ListItem key={path} disablePadding>
            <ListItemButton
              component={NavLink}
              to={path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                mx: 1,
                borderRadius: 2,
                color: 'rgba(255,255,255,0.75)',
                '&.active': {
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#FFFFFF',
                  '& .MuiListItemIcon-root': { color: '#A5D6A7' }
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: '#FFFFFF'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{icon}</ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

      {/* Logout */}
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: 2,
              color: 'rgba(255,255,255,0.75)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: 'primary.dark' }}
        >
          <Toolbar sx={{ minHeight: MOBILE_APP_BAR_HEIGHT }}>
            <Tooltip title="Open menu">
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <SpaIcon sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>
              Farmschool
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar — permanent on desktop, temporary on mobile */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              // Sit above the StudentDrawer modal (the AppBar uses drawer + 1)
              // so the nav menu is usable while a student profile is open.
              zIndex: (t) => t.zIndex.drawer + 2,
              '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' }
            }}
          >
            {drawerContent}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box'
              }
            }}
            open
          >
            {drawerContent}
          </Drawer>
        )}
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: { xs: 8, md: 0 },
          maxWidth: '100%',
          overflow: 'auto'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
